// Desk overdrawn-cash — the single React Query read every Desk surface uses for
// the IM-scoped overdrawn snapshot (GET /api/desk/cash/overdrawn — the DeskScope).
//
// One hook = one query key + one response shape. Three Desk surfaces read this
// feeder — the Dashboard worklist (`my-book`), the Actions worklist, and the
// Overdrawn Cash detail page — and they MUST agree on the cached shape: they all
// key on `['desk','cash','overdrawn', <version>]`, so a page that stored a bare
// `rows` array under the same key another page reads as `{ rows, versionRef }`
// poisoned the shared cache and crashed the reader with "rows.filter is not a
// function" (SEP-165). Funnelling every read through this hook makes the shape
// impossible to diverge. `versionRef` is always present, so a rows-only consumer
// simply reads `.rows`.
//
// The default (no `versionId`) reads the active snapshot under the `'active'`
// key; an explicit version reads that exact snapshot under its own key (a 404
// surfaces as an error, per the contract).

import { queryOptions, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { OverdrawnCashResponse } from '@septima/contract';

/**
 * The signed-in IM's overdrawn-cash snapshot (`{ rows, versionRef }`). Pass a
 * `versionId` to read a specific snapshot; omit it for the active version.
 */
export function useDeskOverdrawnCash(versionId?: string) {
    return useQuery(deskOverdrawnCashQuery(versionId));
}

/** The query behind `useDeskOverdrawnCash`. A data slot reads the same cache entry. */
export function deskOverdrawnCashQuery(versionId?: string) {
    return queryOptions({
        queryKey: ['desk', 'cash', 'overdrawn', versionId || 'active'],
        queryFn: async (): Promise<OverdrawnCashResponse> => {
            const res = await api.cash.listOverdrawnForDesk({
                query: versionId ? { version: versionId } : {},
            });
            if (res.status !== 200) throw new Error(`overdrawn-cash list failed: ${res.status}`);
            return res.body;
        },
    });
}
